// src/features/events/backupSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { backend_url } from "../../App";
import { setSnackBar } from "../../reusable_components/site_data/siteDataSlice";

// ─────────────────────────────────────────────
// THUNKS
// ─────────────────────────────────────────────

/** GET /core/api/settings/ */
export const fetchSystemSettings = createAsyncThunk(
  "backup/fetchSystemSettings",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${backend_url}/core/api/settings/`, {
        headers: { Authorization: `Token ${token}` },
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || err.message || "Failed to fetch settings",
      );
    }
  },
);

/** PATCH /core/api/settings/ */
export const updateSystemSettings = createAsyncThunk(
  "backup/updateSystemSettings",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.patch(
        `${backend_url}/core/api/settings/`,
        payload,
        { headers: { Authorization: `Token ${token}` } },
      );
      dispatch(setSnackBar("Settings saved successfully"));
      return res.data;
    } catch (err) {
      const msg =
        err.response?.data?.detail || err.message || "Failed to save settings";
      dispatch(setSnackBar(msg));
      return rejectWithValue(msg);
    }
  },
);

/** GET /core/api/backup/status/ */
export const fetchBackupStatus = createAsyncThunk(
  "backup/fetchBackupStatus",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${backend_url}/core/api/backup/status/`, {
        headers: { Authorization: `Token ${token}` },
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || err.message || "Failed to fetch status",
      );
    }
  },
);

// ─────────────────────────────────────────────
// POLLING HELPER
// ─────────────────────────────────────────────

/**
 * Polls /backup/status/ every 10s until status is 'success' or 'failed'.
 * Returns a Promise that resolves with the final status object.
 */
const pollUntilDone = (
  dispatch,
  { maxAttempts = 720, intervalMs = 10000 } = {},
) => {
  return new Promise((resolve) => {
    let attempts = 0;
    const token = localStorage.getItem("token");

    const tick = async () => {
      attempts += 1;
      try {
        const res = await axios.get(`${backend_url}/core/api/backup/status/`, {
          headers: { Authorization: `Token ${token}` },
        });
        const status = res.data;

        // Update Redux state so UI can show progress
        dispatch(setBackupStatus(status));

        if (status.last_backup_status === "success") {
          return resolve({ ok: true, status });
        }
        if (status.last_backup_status === "failed") {
          return resolve({ ok: false, status });
        }
        if (attempts >= maxAttempts) {
          return resolve({ ok: false, status, timeout: true });
        }
        setTimeout(tick, intervalMs);
      } catch (e) {
        // network blip — keep trying
        setTimeout(tick, intervalMs);
      }
    };

    tick();
  });
};

// ─────────────────────────────────────────────
// RUN BACKUP (ASYNC)
// ─────────────────────────────────────────────

/** POST /core/api/backup/run/ — async, then poll */
export const runBackup = createAsyncThunk(
  "backup/runBackup",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const token = localStorage.getItem("token");

      // 1. Kick off backup (returns 202 immediately)
      const res = await axios.post(
        `${backend_url}/core/api/backup/run/`,
        {},
        { headers: { Authorization: `Token ${token}` } },
      );

      if (res.status === 409) {
        const msg = "A backup is already in progress.";
        dispatch(setSnackBar(`⚠️ ${msg}`));
        return rejectWithValue(msg);
      }

      if (res.status !== 202) {
        const msg =
          res.data?.message || res.data?.error || "Failed to start backup";
        dispatch(setSnackBar(`❌ ${msg}`));
        return rejectWithValue(msg);
      }

      // 2. Poll until done
      dispatch(setSnackBar("🔄 Backup started in background..."));
      const result = await pollUntilDone(dispatch);

      if (result.ok) {
        dispatch(setSnackBar("✅ Backup completed successfully"));
      } else if (result.timeout) {
        dispatch(setSnackBar("⚠️ Backup still running. Check status later."));
      } else {
        dispatch(
          setSnackBar(
            `❌ Backup failed: ${
              result.status?.last_backup_message || "Unknown error"
            }`,
          ),
        );
      }

      // Refresh status in Redux
      dispatch(fetchBackupStatus());
      return result.status;
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.detail ||
        err.message ||
        "Backup failed";
      dispatch(setSnackBar(`❌ ${msg}`));
      return rejectWithValue(msg);
    }
  },
);

/** POST /core/api/backup/run-and-delete/ — async, then poll */
export const runBackupAndDelete = createAsyncThunk(
  "backup/runBackupAndDelete",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const token = localStorage.getItem("token");

      const res = await axios.post(
        `${backend_url}/core/api/backup/run-and-delete/`,
        {},
        { headers: { Authorization: `Token ${token}` } },
      );

      if (res.status === 409) {
        const msg = "A backup is already in progress.";
        dispatch(setSnackBar(`⚠️ ${msg}`));
        return rejectWithValue(msg);
      }

      if (res.status !== 202) {
        const msg =
          res.data?.message || res.data?.error || "Failed to start backup";
        dispatch(setSnackBar(`❌ ${msg}`));
        return rejectWithValue(msg);
      }

      dispatch(setSnackBar("🔄 Backup + delete started in background..."));
      const result = await pollUntilDone(dispatch);

      if (result.ok) {
        const deleted = result.status?.last_cleanup_count || 0;
        dispatch(setSnackBar(`✅ Backup done + Deleted ${deleted} events`));
      } else if (result.timeout) {
        dispatch(setSnackBar("⚠️ Backup still running. Check status later."));
      } else {
        dispatch(
          setSnackBar(
            `❌ Backup+Delete failed: ${
              result.status?.last_backup_message || "Unknown error"
            }`,
          ),
        );
      }

      dispatch(fetchBackupStatus());
      dispatch(fetchSystemSettings());
      return result.status;
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.detail ||
        err.message ||
        "Backup & Delete failed";
      dispatch(setSnackBar(`❌ ${msg}`));
      return rejectWithValue(msg);
    }
  },
);

// ─────────────────────────────────────────────
// SLICE
// ─────────────────────────────────────────────

const initialState = {
  settings: null,
  status: null,
  loading: false,
  backupRunning: false,
  lastResult: null,
  error: null,
};

export const backupSlice = createSlice({
  name: "backup",
  initialState,
  reducers: {
    clearBackupError: (state) => {
      state.error = null;
    },
    clearLastResult: (state) => {
      state.lastResult = null;
    },
    // used by pollUntilDone to push fresh status into Redux
    setBackupStatus: (state, action) => {
      state.status = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSystemSettings.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSystemSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = action.payload;
      })
      .addCase(fetchSystemSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateSystemSettings.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateSystemSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = { ...state.settings, ...action.payload };
      })
      .addCase(updateSystemSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchBackupStatus.fulfilled, (state, action) => {
        state.status = action.payload;
      })

      .addCase(runBackup.pending, (state) => {
        state.backupRunning = true;
      })
      .addCase(runBackup.fulfilled, (state, action) => {
        state.backupRunning = false;
        state.lastResult = action.payload;
      })
      .addCase(runBackup.rejected, (state, action) => {
        state.backupRunning = false;
        state.error = action.payload;
      })

      .addCase(runBackupAndDelete.pending, (state) => {
        state.backupRunning = true;
      })
      .addCase(runBackupAndDelete.fulfilled, (state, action) => {
        state.backupRunning = false;
        state.lastResult = action.payload;
      })
      .addCase(runBackupAndDelete.rejected, (state, action) => {
        state.backupRunning = false;
        state.error = action.payload;
      });
  },
});

export const { clearBackupError, clearLastResult, setBackupStatus } =
  backupSlice.actions;

// Selectors
export const selectBackup = (state) => state.backup;
export const selectBackupSettings = (state) => state.backup.settings;
export const selectBackupStatus = (state) => state.backup.status;
export const selectBackupRunning = (state) => state.backup.backupRunning;

export default backupSlice.reducer;
